import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import {
  LineChart, Line, BarChart, Bar, ScatterChart, Scatter,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ZAxis
} from 'recharts';

const UsageFeatureAdoptionPage = () => {
  const [daaData, setDaaData] = useState(null);
  const [maaData, setMaaData] = useState(null);
  const [loginFreqData, setLoginFreqData] = useState(null);
  const [peakUsageData, setPeakUsageData] = useState(null);
  const [pageVisitsData, setPageVisitsData] = useState(null);
  const [featureOrgData, setFeatureOrgData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDAA();
    fetchMAA();
    fetchLoginFrequency();
    fetchPeakUsage();
    fetchPageVisits();
    fetchFeatureOrgSize();
  }, []);

  const fetchDAA = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/daily-active-admins`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setDaaData(response.data);
    } catch (err) {
      console.error('Failed to fetch DAA:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMAA = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/monthly-active-admins`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setMaaData(response.data);
    } catch (err) {
      console.error('Failed to fetch MAA:', err);
    }
  };

  const fetchLoginFrequency = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/login-frequency`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setLoginFreqData(response.data);
    } catch (err) {
      console.error('Failed to fetch login frequency:', err);
    }
  };


  const fetchPeakUsage = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/peak-usage-times`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPeakUsageData(response.data);
    } catch (err) {
      console.error('Failed to fetch peak usage:', err);
    }
  };

  const fetchPageVisits = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/most-visited-pages`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setPageVisitsData(response.data);
    } catch (err) {
      console.error('Failed to fetch page visits:', err);
    }
  };

  const fetchFeatureOrgSize = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/admin-portal/feature-usage-by-org-size`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setFeatureOrgData(response.data);
    } catch (err) {
      console.error('Failed to fetch feature org data:', err);
    }
  };




  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <span className="ml-3 text-gray-600">Loading usage analytics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
          <h1 className="text-3xl font-bold">Usage & Feature Adoption</h1>
          <p className="text-purple-200 text-sm">Admin portal engagement and feature usage analytics</p>
        </div>
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={fetchDAA}
            className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white shadow-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <div>
              <h1 className="text-3xl font-bold">Usage & Feature Adoption</h1>
              <p className="text-purple-200 text-sm">Admin portal engagement and feature usage analytics</p>
            </div>
          </div>
          <button
            onClick={() => {
              fetchDAA();
              fetchMAA();
              fetchLoginFrequency();
              fetchPeakUsage();
              fetchPageVisits();
              fetchFeatureOrgSize();
            }}
            className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-white font-medium transition-all flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Admin Users</p>
              <p className="text-3xl font-bold text-gray-900">{daaData.total_admins}</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50">
              <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg. Daily Active</p>
              <p className="text-3xl font-bold text-gray-900">
                {daaData.daily_data ? Math.round(daaData.daily_data.reduce((sum, day) => sum + day.active_admins, 0) / 30) : 0}
              </p>
            </div>
            <div className="p-3 rounded-lg bg-blue-50">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Tracking Period</p>
              <p className="text-3xl font-bold text-gray-900">30</p>
              <p className="text-xs text-gray-500">days</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Chart 1: Daily Active Admins Trend */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Active Admins (DAA) - Last 30 Days</h3>
        {daaData && daaData.daily_data && daaData.daily_data.length > 0 ? (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={daaData.daily_data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="active_admins" stroke="#8b5cf6" strokeWidth={2} name="Active Admins" />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-[350px] flex items-center justify-center text-gray-500">
            No activity data available for the last 30 days.
          </div>
        )}
        <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>What this shows:</strong> The number of admin users who logged into or were active in the portal each day over the last 30 days.
          </p>
          <p className="text-sm text-gray-700 mt-2">
            <strong>How to interpret:</strong> Consistent daily activity indicates strong portal adoption. Weekday spikes with weekend drops are normal for business tools. Declining trend suggests admins aren't finding value or are facing usability issues. Zero-admin days may indicate system downtime or access problems. Aim for steady or growing DAA as your customer base expands.
          </p>
        </div>
      </div>



      {/* Chart 2: Monthly Active Admins Trend */}
      {maaData && maaData.monthly_data && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Monthly Active Admins (MAA) - Last 12 Months</h3>
          {maaData.monthly_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <LineChart data={maaData.monthly_data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="active_admins" stroke="#3b82f6" strokeWidth={2} name="Active Admins" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-gray-500">
              No activity data available for the last 12 months.
            </div>
          )}
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>What this shows:</strong> The number of unique admin users who were active in the portal each month over the last 12 months.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <strong>How to interpret:</strong> Growing MAA indicates successful customer acquisition and portal adoption. Flat or declining MAA suggests churn or stagnant growth - focus on retention and new customer onboarding. Compare MAA growth to customer acquisition rate - if you're adding organizations but MAA isn't growing, admins aren't engaging with the portal.
            </p>
          </div>
        </div>
      )}



      {/* Chart 3: Admin Login Frequency Distribution */}
      {loginFreqData && loginFreqData.frequency_distribution && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Login Frequency Distribution (Last 4 Weeks)</h3>
          {Object.keys(loginFreqData.frequency_distribution).length > 0 ? (
            <ResponsiveContainer width="100%" height={350}>
              <BarChart 
                data={Object.entries(loginFreqData.frequency_distribution).map(([range, count]) => ({
                  range,
                  count
                }))}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="range" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="count" fill="#10b981" name="Number of Admins" />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-gray-500">
              No login frequency data available.
            </div>
          )}
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>What this shows:</strong> How many admins fall into each login frequency category (0, 1-2, 3-5, 6-10, 11+ logins) over the last 4 weeks.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <strong>How to interpret:</strong> Many admins in "0 logins" suggests poor onboarding or portal value issues - reach out to inactive admins. Most in "1-2 logins" means task-oriented usage (good for specific workflows). High frequency (6-10, 11+) shows power users who rely on the portal daily - these are your champions. Aim to move admins from low to high frequency through better features and engagement.
            </p>
          </div>
        </div>
      )}



      {/* Chart 4: Peak Usage Times Heatmap */}
      {peakUsageData && peakUsageData.heatmap_data && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Peak Usage Times Heatmap (Last 30 Days)</h3>
          {peakUsageData.heatmap_data.length > 0 ? (
            <div className="overflow-x-auto">
              <div className="text-xs text-gray-600 mb-2">Activity by Day of Week and Hour</div>
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="border border-gray-300 p-2 bg-gray-50 text-left text-xs font-semibold">Hour</th>
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(day => (
                      <th key={day} className="border border-gray-300 p-2 bg-gray-50 text-center text-xs font-semibold">{day}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[...Array(24)].map((_, hour) => (
                    <tr key={hour}>
                      <td className="border border-gray-300 p-2 text-xs font-medium text-gray-700">
                        {hour.toString().padStart(2, '0')}:00
                      </td>
                      {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map(day => {
                        const cell = peakUsageData.heatmap_data.find(d => d.day === day && d.hour === hour);
                        const activity = cell ? cell.activity : 0;
                        const maxActivity = Math.max(...peakUsageData.heatmap_data.map(d => d.activity));
                        const intensity = maxActivity > 0 ? activity / maxActivity : 0;
                        
                        // Color intensity from white (0) to purple (high)
                        let bgColor = 'bg-white';
                        if (intensity > 0.8) bgColor = 'bg-purple-700';
                        else if (intensity > 0.6) bgColor = 'bg-purple-500';
                        else if (intensity > 0.4) bgColor = 'bg-purple-400';
                        else if (intensity > 0.2) bgColor = 'bg-purple-200';
                        else if (intensity > 0) bgColor = 'bg-purple-100';
                        
                        return (
                          <td key={day} className={`border border-gray-300 p-2 text-center text-xs ${bgColor} ${intensity > 0.5 ? 'text-white' : 'text-gray-700'}`}>
                            {activity}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex items-center justify-end text-xs text-gray-600">
                <span className="mr-2">Low</span>
                <div className="flex space-x-1">
                  <div className="w-4 h-4 bg-purple-100 border border-gray-300"></div>
                  <div className="w-4 h-4 bg-purple-200 border border-gray-300"></div>
                  <div className="w-4 h-4 bg-purple-400 border border-gray-300"></div>
                  <div className="w-4 h-4 bg-purple-500 border border-gray-300"></div>
                  <div className="w-4 h-4 bg-purple-700 border border-gray-300"></div>
                </div>
                <span className="ml-2">High</span>
              </div>
            </div>
          ) : (
            <div className="h-[350px] flex items-center justify-center text-gray-500">
              No usage time data available.
            </div>
          )}
          <div className="mt-4 p-4 bg-indigo-50 border border-indigo-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>What this shows:</strong> A heatmap showing when admins are most active in the portal by day of week (rows) and hour of day (columns). Darker purple indicates higher activity.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <strong>How to interpret:</strong> Identify peak usage times for support staffing and optimal times for system maintenance. Dark cells during business hours (9-17) are expected. Activity outside business hours may indicate overtime work or international users. Plan updates/maintenance during low-activity periods (light cells). Use peak times for live demos, training sessions, or support availability.
            </p>
          </div>
        </div>
      )}



      {/* Chart 5: Most Visited Pages */}
      {pageVisitsData && pageVisitsData.page_visits && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Visited Pages/Sections (Last 30 Days)</h3>
          <ResponsiveContainer width="100%" height={600}>
            <BarChart 
              data={pageVisitsData.page_visits}
              layout="vertical"
              margin={{ left: 10, right: 20, top: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis 
                dataKey="page" 
                type="category" 
                width={170} 
                style={{ fontSize: '12px' }}
                tick={{ fill: '#374151' }}
              />
              <Tooltip />
              <Legend />
              <Bar dataKey="visits" fill="#f59e0b" name="Page Views" />
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>What this shows:</strong> Actual page visits for each section of the admin portal over the last 30 days. Pages with 0 views indicate no engagement.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <strong>How to interpret:</strong> High activity pages are your core features - ensure they're well-optimized and stable. Zero or low activity features may be underutilized (need better placement/promotion), not valuable (consider removal), or have discoverability issues. Use this to prioritize development resources, bug fixes, and UI improvements. Pages with consistently zero views should be investigated - are they broken, hidden in the menu, or unnecessary?
            </p>
          </div>
        </div>
      )}



      {/* Chart 6: Feature Usage by Organisation Size */}
      {featureOrgData && featureOrgData.org_data && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Feature Usage by Organisation Size</h3>
          {featureOrgData.org_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={400}>
              <ScatterChart margin={{ left: 20, right: 20, top: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  type="number" 
                  dataKey="team_size" 
                  name="Team Size" 
                  label={{ value: 'Team Size (Users)', position: 'insideBottom', offset: -10 }}
                />
                <YAxis 
                  type="number" 
                  dataKey="features_used" 
                  name="Features Used"
                  label={{ value: 'Features Used', angle: -90, position: 'insideLeft' }}
                />
                <ZAxis range={[100, 400]} />
                <Tooltip 
                  cursor={{ strokeDasharray: '3 3' }}
                  content={({ active, payload }) => {
                    if (active && payload && payload.length) {
                      const data = payload[0].payload;
                      return (
                        <div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
                          <p className="font-semibold text-gray-900">{data.organisation}</p>
                          <p className="text-sm text-gray-700">Team Size: {data.team_size}</p>
                          <p className="text-sm text-gray-700">Features Used: {data.features_used}</p>
                        </div>
                      );
                    }
                    return null;
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: '30px' }} />
                <Scatter name="Organisations" data={featureOrgData.org_data} fill="#8b5cf6" />
              </ScatterChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[400px] flex items-center justify-center text-gray-500">
              No organisation data available.
            </div>
          )}
          <div className="mt-4 p-4 bg-purple-50 border border-purple-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>What this shows:</strong> A scatter plot showing the relationship between organisation size (number of team members) and the number of portal features they use. Each dot represents one organisation.
            </p>
            <p className="text-sm text-gray-700 mt-2">
              <strong>How to interpret:</strong> Positive correlation (larger orgs use more features) indicates features scale well with company size. If small orgs use many features, your onboarding is excellent. If large orgs use few features, they may not know about available tools - improve feature discovery and admin training. Outliers (large org, few features OR small org, many features) warrant investigation - are they power users or struggling users?
            </p>
          </div>
        </div>
      )}

    </div>
  );
};

export default UsageFeatureAdoptionPage;
